import _Store from '../../store/store'
import {
  Invoice,
  InvoiceDocument,
  InvoiceUpdate,
} from '../api'
import { InvoiceV2 } from "../api-v2"
import { findTag, transformV2RawInvoice } from "../api-v2/helpers"
import { mapBypassVerificationInvoiceV1RequestToV2Request, mapCreateInvoiceV1RequestToV2Request, mapPurchaseInvoiceV1RequestToV2Request, mapRegenerateInvoiceDocumentV1RequestToV2Request, mapRejectInvoiceV1RequestToV2Request, mapRevertInvoiceV1RequestToV2Request, mapVerifyInvoiceV1RequestToV2Request, mapWaitOnVerificationInvoiceV1RequestToV2Request, mapUpdateInvoiceV1RequestToV2Request, mapActivitiesToInvoiceUpdates } from "../api-v2/mappings"
import { CError } from '../consoleMixin'
import { captureSentryEvent } from '../sentryMixin'
import { ApiVersionDelagator } from "./api-delegator"
/**
 * @typedef {import('../api-v2/js-doc').UiInvoiceModel} UiInvoiceModel
 * @typedef {import('../api-v2/js-doc').UiInvoicePossibleDuplicate} UiInvoicePossibleDuplicate
 * @typedef {import('../api-v2/js-doc').UIInvoiceUpdates} UIInvoiceUpdates
 * @typedef {import('../api-v2/js-doc').V1CreateInvoiceRequest} V1CreateInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1UpdateInvoiceRequest} V1UpdateInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1PurchaseInvoiceRequest} V1PurchaseInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1RejectInvoiceRequest} V1RejectInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1VerifyInvoiceRequest} V1VerifyInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1WaitOnVerificationInvoiceRequest} V1WaitOnVerificationInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1BypassVerificationInvoiceRequest} V1BypassInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1InvoiceResponse} V1InvoiceResponse
 * @typedef {import('../api-v2/js-doc').V1RevertInvoiceRequest} V1RevertInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V1RegenerateInvoiceDocumentRequest} V1RegenerateInvoiceDocumentRequest
 * @typedef {import('../api-v2/js-doc').V1InvoiceUpdateResponse} V1InvoiceUpdateResponse
 * @typedef {import('../api-v2/js-doc').V2VerifyInvoiceRequest} V2VerifyInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V2CreateInvoiceRequest} V2CreateInvoiceRequest
 * @typedef {import('../api-v2/js-doc').V2InvoiceResponse} V2InvoiceResponse
 * @typedef {import('../api-v2/js-doc').V2CreateInvoiceDocumentRequest} V2CreateInvoiceDocumentRequest
 * @typedef {import('../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 * @typedef {import('../api-v2/js-doc').V2PossibleDuplicatesResponse} V2PossibleDuplicatesResponse
 */

export class InvoiceApiDelegator extends ApiVersionDelagator {
  /**
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request
   * @returns {Promise<UiInvoiceModel>}
   */
  async postInvoice (v1Request) {
    return await this.runWriteApiCalls(
      v1Request.client_id,
      v1Request,
      this.#postInvoiceV1.bind(this),
      this.#postInvoiceV2.bind(this),
      {
        operationName: 'post-invoice',
      }
    )
  }

  /**
   * @param {V1CreateInvoiceRequest} payload - V1 create invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #postInvoiceV1 (payload) {
    const v1Payload = { ...payload }
    if (payload.debtor_id === _Store.debtorNotFound.id) {
      v1Payload.debtor_id = await this.getUnknownDebtorID()
    }

    return (await Invoice.create(v1Payload)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request body
   * @param {V1InvoiceResponse=} v1InvoiceResponse - V1 create invoice response body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #postInvoiceV2 (v2MappingOptions, v1Request, v1InvoiceResponse) {
    const v2Request = mapCreateInvoiceV1RequestToV2Request(v2MappingOptions, v1Request, v1InvoiceResponse)
    const result = await new InvoiceV2().create(v2Request)
    if (!await this.isV2ListingEnabled()) {
      return (await Invoice.get(result.id)).data
    }
    return transformV2RawInvoice(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {string} id - ID of the invoice
   * @returns {Promise<void>}
   */
  async deleteInvoice (clientId, id) {
    return await this.runWriteApiCalls(
      clientId,
      id,
      this.#deleteInvoiceV1,
      this.#deleteInvoiceV2,
      {
        operationName: 'delete-invoice',
      }
    )
  }

  /**
   * @param {string} id - ID of the invoice
   * @returns {Promise<void>}
   */
  async #deleteInvoiceV1 (id) {
    await Invoice.delete(id)
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {string} id - ID of the invoice
   * @returns {Promise<void>}
   */
  async #deleteInvoiceV2 (v2MappingOptions, id) {
    await new InvoiceV2().delete(id, { __ingestThrough: v2MappingOptions.ingestThrough })
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1PurchaseInvoiceRequest} v1Request -  V1 purchase request body
   * @returns {Promise<V1InvoiceResponse>}
   */
  async purchaseInvoice (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#purchaseInvoiceV1,
      this.#purchaseInvoiceV2,
      {
        operationName: 'purchase-invoice',
      }
    )
  }

  /**
   * @param {V1PurchaseInvoiceRequest} v1Request - V1 purchase request body
   * @returns {Promise<V1InvoiceResponse>}
   */
  async #purchaseInvoiceV1 (v1Request) {
    return (await Invoice.update(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1PurchaseInvoiceRequest} v1Request - V1 purchase request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #purchaseInvoiceV2 (v2MappingOptions, v1Request) {
    const v2Request = mapPurchaseInvoiceV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new InvoiceV2().purchase(v1Request.id, v2Request)
    return transformV2RawInvoice(result)
  }

  /**
   * @param {V1VerifyInvoiceRequest} v1Request - V1 verify invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async verifyInvoice (v1Request) {
    return await this.runWriteApiCalls(
      v1Request.client_id,
      v1Request,
      this.#verifyInvoiceV1,
      this.#verifyInvoiceV2.bind(this),
      {
        operationName: 'verify-invoice',
      }
    )
  }

  /**
   * @param {V1VerifyInvoiceRequest} v1Request - V1 verify invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #verifyInvoiceV1 (v1Request) {
    return (await Invoice.update(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1VerifyInvoiceRequest} v1Request - V1 verify invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #verifyInvoiceV2 (v2MappingOptions, v1Request) {
    const v2Request = mapVerifyInvoiceV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new InvoiceV2().verify(v1Request.id, v2Request)
    if (!await this.isV2ListingEnabled()) {
      return (await Invoice.get(result.id)).data
    }
    return transformV2RawInvoice(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1BypassInvoiceRequest} v1Request - V1 bypass invoice verification request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async bypassVerification (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#bypassVerificationV1,
      this.#bypassVerificationV2.bind(this),
      {
        operationName: 'bypass-verification',
      }
    )
  }

  /**
   * @param {V1BypassInvoiceRequest} v1Request - V1 bypass invoice verification request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #bypassVerificationV1 (v1Request) {
    return (await Invoice.update(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1BypassInvoiceRequest} v1Request - V1 bypass invoice verification request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #bypassVerificationV2 (v2MappingOptions, v1Request) {
    const v2Request = mapBypassVerificationInvoiceV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new InvoiceV2().verify(v1Request.id, v2Request)
    if (!await this.isV2ListingEnabled()) {
      return (await Invoice.get(result.id)).data
    }
    return transformV2RawInvoice(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1WaitOnVerificationInvoiceRequest} v1Request - V1 wait for verification request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async waitForVerification (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#waitForVerificationV1,
      this.#waitForVerificationV2.bind(this),
      {
        operationName: 'wait-for-verification',
      }
    )
  }

  /**
   * @param {V1WaitOnVerificationInvoiceRequest} v1Request - V1 wait for verification request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #waitForVerificationV1 (v1Request) {
    return (await InvoiceUpdate.create(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1WaitOnVerificationInvoiceRequest} v1Request - V1 wait for verification request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #waitForVerificationV2 (v2MappingOptions, v1Request) {
    const v2Request = mapWaitOnVerificationInvoiceV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new InvoiceV2().verify(v1Request.invoice_id, v2Request)
    if (!await this.isV2ListingEnabled()) {
      const invoice = (await Invoice.get(result.id)).data
      return invoice.invoice_updates[0]
    }
    //TODO: This actually needs to return an invoice update as V1 does that
    return transformV2RawInvoice(result).invoice_updates[0]
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1RejectInvoiceRequest} v1Request - V1 reject invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async rejectInvoice (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#rejectInvoiceV1,
      this.#rejectInvoiceV2,
      {
        operationName: 'reject-invoice',
      }
    )
  }

  /**
   * @param {V1RejectInvoiceRequest} v1Request - V1 reject invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #rejectInvoiceV1 (v1Request) {
    return (await Invoice.update(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1RejectInvoiceRequest} v1Request - V1 reject invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #rejectInvoiceV2 (v2MappingOptions, v1Request) {
    const v2Request = mapRejectInvoiceV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new InvoiceV2().reject(v1Request.id, v2Request)
    return transformV2RawInvoice(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1RevertInvoiceRequest} v1Request - V1 revert invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async revertInvoice (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#revertInvoiceV1,
      this.#revertInvoiceV2,
      {
        operationName: 'revert-invoice',
      }
    )
  }

  /**
   * @param {V1RevertInvoiceRequest} v1Request - V1 revert invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #revertInvoiceV1 (v1Request) {
    return (await Invoice.update(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1RevertInvoiceRequest} v1Request - V1 revert invoice request body
   * @returns {Promise<UiInvoiceModel>}
   */
  async #revertInvoiceV2 (v2MappingOptions, v1Request) {
    const v2Request = mapRevertInvoiceV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new InvoiceV2().revert(v1Request.id, v2Request)
    return transformV2RawInvoice(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1RegenerateInvoiceDocumentRequest} v1Request - V1 regenerate invoice document request body
   * @returns {Promise<void>}
   */
  async regenerateDocs (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#regenerateDocsV1,
      this.#regenerateDocsV2,
      {
        operationName: 'regenerate-docs',
      }
    )
  }

  /**
   * @param {V1RegenerateInvoiceDocumentRequest} v1Request - V1 regenerate invoice document request body
   * @returns {Promise<void>}
   */
  async #regenerateDocsV1 (v1Request) {
    return InvoiceDocument.regenerateDocs(v1Request.id)
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1RegenerateInvoiceDocumentRequest} v1Request - V1 regenerate invoice document request body
   * @returns {Promise<void>}
   */
  async #regenerateDocsV2 (v2MappingOptions, v1Request) {
    const v2Request = mapRegenerateInvoiceDocumentV1RequestToV2Request(v2MappingOptions, v1Request)
    await new InvoiceV2().regenerateDocs(v1Request.id, v2Request)
  }

  /**
   * @param {V1UpdateInvoiceRequest} v1Request - V1 update invoice request
   * @returns {Promise<UiInvoiceModel>}
   */
  async updateInvoice (v1Request) {
    return await this.runWriteApiCalls(
      v1Request.client_id,
      v1Request,
      this.#updateInvoiceV1.bind(this),
      this.#updateInvoiceV2.bind(this),
      {
        operationName: 'update-invoice',
      }
    )
  }

  /**
   * @param {V1UpdateInvoiceRequest} v1Request - V1 update invoice request
   * @returns {Promise<UiInvoiceModel>}
   */
  async #updateInvoiceV1 (v1Request) {
    // When `use-v2` and `listing` is enabled in LD, we use a static
    // object for debtor not found, but V1 still needs the real debtor
    // not found's ID to create an invoice, which the V2 POST
    // request relies on for the ID and display_id. By creating
    // a copy of payload, we can avoid having to manipulate the payload
    // when using it in postInvoiceV2

    // Shallow copy is good enough
    const v1Payload = { ...v1Request }
    if (v1Request.debtor_id === _Store.debtorNotFound.id) {
      v1Payload.debtor_id = await this.getUnknownDebtorID()
    }


    try {
      const promises = []
      v1Request.documents.adding.forEach((document) => {
        promises.push(
          InvoiceDocument.create({
            ...document,
            invoice_id: v1Request.id,
          })
        )
      })
      v1Request.documents.removing.forEach((documentID) => {
        promises.push(InvoiceDocument.delete(documentID))
      })

      await Promise.all(promises)
    }
    catch (error) {
      captureSentryEvent(
        'Invoice Create "Submit Docs" Error',
        {
          config: error.config,
          data: v1Request,
          details: error,
          documents: v1Request.documents,
          response: error.response,
        }
      )
      CError(error)
    }
    const invoice = (await Invoice.update(v1Payload)).data
    return invoice
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - v2 mapping options
   * @param {V1UpdateInvoiceRequest} v1Request - V1 update invoice request
   * @param {any=} v1Response - V1 update invoice response
   * @returns {Promise<UiInvoiceModel>}
   */
  async #updateInvoiceV2 (v2MappingOptions, v1Request, v1Response) {
    const v2Request = mapUpdateInvoiceV1RequestToV2Request(v2MappingOptions, v1Request, v1Response)
    const result = await new InvoiceV2().update(v1Request.id, v2Request)
    if (!await this.isV2ListingEnabled()) {
      return (await Invoice.get(result.id)).data
    }
    return transformV2RawInvoice(result)
  }

  /**
   * @param {string} clientId - Client ID
   * @param {any} v1Request - V1 create invoice update request
   * @returns {Promise<V1InvoiceUpdateResponse>}
   */
  async postInvoiceUpdate (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#postInvoiceUpdateV1,
      this.#postInvoiceUpdateV2.bind(this),
      {
        operationName: 'post-invoice-update',
      }
    )
  }

  /**
   * @param {any} v1Request - V1 create invoice update request
   * @returns {Promise<V1InvoiceUpdateResponse>}
   */
  async #postInvoiceUpdateV1 (v1Request) {
    return (await InvoiceUpdate.create(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {any} v1Request - V1 create invoice update request
   * @param {any=} v1Response - V1 create invoice update response
   * @returns {Promise<V1InvoiceUpdateResponse>}
   */
  async #postInvoiceUpdateV2 (v2MappingOptions, v1Request, v1Response) {
    const key = findTag(v1Response?.update_status || v1Request.update_status)
    const v2Payload = {
      __ingestThrough: v2MappingOptions.ingestThrough,
      __v1Payload: v1Request,
      id: v1Response?.id,
      key,
      note: v1Response?.notes || v1Request.notes,
      payload: {
        "talked_to": v1Response?.talked_to || v1Request.talked_to,
        "talked_to_contact_method": v1Response?.talked_to_contact_method || v1Request.talked_to_contact_method,
        "update_status": v1Response?.update_status || v1Request.update_status,
        "update_type": v1Response?.update_type || v1Request.update_type,
        "check_number_or_ach": v1Response?.check_number_or_ach || v1Request.check_number_or_ach,
        "issue date": v1Response
          ? v1Response['issue date']
          : v1Request['issue date'],
        missing: v1Response?.missing || v1Request.missing,
        metadata: v1Response?.metadata || v1Request.metadata,
      },
    }
    if (v1Request.update_status === 'waiting on verification') {
      /** @type {V1WaitOnVerificationInvoiceRequest} */
      const waitForVerificationPayload = {
        invoice_id: v1Request.invoice_id,
        talked_to: v2Payload.payload.talked_to,
        talked_to_contact_method: v2Payload.payload.talked_to_contact_method,
        notes: v2Payload.note,
        update_type: v1Response?.update_type || v1Request.update_type,
        update_status: v1Response?.update_status || v1Request.update_status,
      }
      return await this.#waitForVerificationV2(v2MappingOptions, waitForVerificationPayload)
    }
    const result = await new InvoiceV2().addTag(v1Request.invoice_id, v2Payload)
    const invoiceUpdates = mapActivitiesToInvoiceUpdates(result)
    return invoiceUpdates[invoiceUpdates.length - 1]
  }

  /**
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request
   * @returns {Promise<UiInvoicePossibleDuplicate[]>}
   */
  async getPossibleDuplicates (v1Request) {
    return this.runGetApiCalls(
      () => {
        return this.#getPossibleDuplicatesV1(v1Request)
      },
      () => {
        return this.#getPossibleDuplicatesV2(v1Request)
      }
    )
  }

  /**
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request
   * @returns {Promise<UiInvoicePossibleDuplicate[]>}
   */
  async #getPossibleDuplicatesV1 (v1Request) {
    const result = (await Invoice.getPossibleDuplicates({
      client_id: v1Request.client_id,
      debtor_id: v1Request.debtor_id,
      load_number: v1Request.load_number,
    })).data
    return result
  }


  /**
   * @param {V1CreateInvoiceRequest} v1Request - V1 create invoice request
   * @returns {Promise<UiInvoicePossibleDuplicate[]>}
   */
  async #getPossibleDuplicatesV2 (v1Request) {
    const v2Request = mapCreateInvoiceV1RequestToV2Request({ ingestThrough: false }, v1Request)
    return (await new InvoiceV2().getPossibleDuplicates(v2Request)).items.map((item) => {
      return {
        load_number: item.invoice?.loadNumber,
        id: item.invoice?.id,
      }
    })
  }
}
